/* eslint-disable max-len */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SealOfBiliteracyLayout from 'src/components/FormLayouts/SealOfBiliteracy/SOBLayout';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import Modal from '../../../../components/Modal/Modal';
import {
  SealOfBiliteracyProvider,
  useSealOfBiliteracyContext,
} from '../../../../contexts/SealOfBiliteracyContext';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import {
  clearDetail,
  selectSealOfBiliteracyDetail,
  selectSealOfBiliteracyForm,
  selectSealOfBiliteracyListItems,
  selectSealOfBiliteracyLoadingDetail,
  selectSealOfBiliteracyLoadingForm,
  selectSealOfBiliteracyLoadingStudentFiles,
  setDetail,
  setStudentFiles as setStudentFilesRedux,
} from '../../../../redux/slices/sealOfBiliteracySlice';
import {
  fetchSealOfBiliteracyById,
  fetchSealOfBiliteracyFilesByStudentThunk,
} from '../../../../redux/thunks/sealOfBiliteracyThunks';
import { appRoutePaths } from '../../../../services/route.service';
import {
  postSealOfBiliteracy,
  putSealOfBiliteracy,
} from '../../../../services/sealOfBiliteracy.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { ApiResponseModel } from '../../../../types/ApiResponseModel';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { DialogType } from '../../../../types/DialogTypes';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { SealOfBiliteracyModel } from '../../../../types/SealOfBiliteracyModel';
import { formatNumericValue } from '../../../../utilities/formUtilities';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';

const SealOfBiliteracyInputContent = (): ReactElement => {
  const { schoolYear } = useParams<{ schoolYear: string }>();
  const dispatch = useAppDispatch();
  const detail: SealOfBiliteracyModel | null = useAppSelector(
    selectSealOfBiliteracyDetail
  );
  const selectedDistrict = useAppSelector(selectSelectedDistrict);
  const listItems = useAppSelector(selectSealOfBiliteracyListItems);
  const formViewModel = useAppSelector(selectSealOfBiliteracyForm);

  const isLoadingDetail = useAppSelector(selectSealOfBiliteracyLoadingDetail);
  const isFormViewLoading = useAppSelector(selectSealOfBiliteracyLoadingForm);
  const isLoadingStudentFiles = useAppSelector(
    selectSealOfBiliteracyLoadingStudentFiles
  );
  const isLoading =
    isLoadingDetail || isFormViewLoading || isLoadingStudentFiles;

  const { dynamicValidationSchema, setStudentFiles, resetFormState } =
    useSealOfBiliteracyContext();

  const [showDuplicateErrorModal, setShowDuplicateErrorModal] = useState(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState('');

  const supportedNonGenericErrors = [
    {
      statusCode: 400,
    },
  ];

  const loadFormDetailThunk = createAsyncThunk(
    'sealOfBiliteracy/noOpLoadFormDetail',
    async (): Promise<unknown> => {
      return Promise.resolve({} as unknown);
    }
  );

  const initialFormValues = detail || {
    id: '',
    studentIdentifier: '',
    studentLastOrSurname: '',
    studentFirstName: '',
    studentMiddleName: '',
    districtName: '',
    schoolName: '',
    schoolYear: schoolYear || '',
    leaIdentifier: selectedDistrict.districtId || '',
    schoolIdentifier: '',
    sealAwarded: '',
    englishAssessmentName: '',
    languageAssessmentName: '',
    languageAssessmentCode: '',
    englishAssessmentMeasures: [],
    languageAssessmentMeasures: [],
  };

  // Helper function to format numeric measures before saving
  const formatNumericMeasures = (
    formValues: SealOfBiliteracyModel
  ): SealOfBiliteracyModel => {
    const formattedValues = { ...formValues };

    // Format English assessment measures
    if (formattedValues.englishAssessmentMeasures) {
      formattedValues.englishAssessmentMeasures =
        formattedValues.englishAssessmentMeasures.map((measure) => ({
          ...measure,
          measureValue: measure.measureValue
            ? formatNumericValue(measure.measureValue)
            : measure.measureValue,
        }));
    }

    // Format Language assessment measures
    if (formattedValues.languageAssessmentMeasures) {
      formattedValues.languageAssessmentMeasures =
        formattedValues.languageAssessmentMeasures.map((measure) => ({
          ...measure,
          measureValue: measure.measureValue
            ? formatNumericValue(measure.measureValue)
            : measure.measureValue,
        }));
    }

    return formattedValues;
  };

  const getLanguageDescription = (languageCode: string): string => {
    if (!formViewModel?.languageOptions || !languageCode) {
      return languageCode || '';
    }
    const languageOption = formViewModel.languageOptions.find(
      (option) => option.value === languageCode
    );
    return languageOption?.text || languageCode;
  };

  const checkForDuplicate = (
    formValues: SealOfBiliteracyModel,
    isEdit: boolean
  ): { isDuplicate: boolean; errorMessage: string } => {
    if (!listItems || listItems.length === 0) {
      return { isDuplicate: false, errorMessage: '' };
    }

    if (
      !formValues.studentIdentifier ||
      !formValues.englishAssessmentName ||
      !formValues.languageAssessmentName ||
      !formValues.languageAssessmentCode
    ) {
      return { isDuplicate: false, errorMessage: '' };
    }

    const duplicate = listItems.find((item) => {
      if (isEdit && item.id === formValues.id) {
        return false;
      }

      return (
        item.studentIdentifier === formValues.studentIdentifier &&
        item.englishAssessmentName === formValues.englishAssessmentName &&
        item.languageAssessmentName === formValues.languageAssessmentName &&
        item.languageAssessmentCode === formValues.languageAssessmentCode
      );
    });

    if (duplicate) {
      const testType = formValues.englishAssessmentName || '';
      const assessmentType = formValues.languageAssessmentName || '';
      const language = getLanguageDescription(
        formValues.languageAssessmentCode || ''
      );

      const errorMessage = `This student already has an entry for Test Type ${testType} and Assessment Type ${assessmentType}, Language ${language}.`;
      return { isDuplicate: true, errorMessage };
    }

    return { isDuplicate: false, errorMessage: '' };
  };

  useEffect(() => {
    if (
      schoolYear &&
      selectedDistrict.districtId &&
      detail?.studentIdentifier
    ) {
      dispatch(
        fetchSealOfBiliteracyFilesByStudentThunk({
          schoolYear,
          leaId: selectedDistrict.districtId,
          msisId: detail.studentIdentifier,
          showLoading: true,
        })
      ).then((result) => {
        if (fetchSealOfBiliteracyFilesByStudentThunk.fulfilled.match(result)) {
          setStudentFiles(result.payload);
        }
      });
    }
  }, [
    dispatch,
    schoolYear,
    selectedDistrict.districtId,
    detail?.studentIdentifier,
    setStudentFiles,
  ]);

  // Clear detail state when component unmounts (e.g., when canceling)
  useEffect(() => {
    return () => {
      dispatch(clearDetail());
      dispatch(setStudentFilesRedux([]));
    };
  }, [dispatch]);

  return (
    <>
      <ManualInputPage<SealOfBiliteracyModel, unknown>
        actions={ManualInputCrudAction.CREATE_AND_EDIT}
        loadingOptions={{
          isLoading: isLoading,
          loadingDataId: 'seal-of-biliteracy-input-loader',
        }}
        navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
        classes={{ pageClass: 'data-input--seal-of-biliteracy--input' }}
        parameterOptions={{
          routeParameterName: 'sealOfBiliteracyId',
          identityName: detail?.id || '',
        }}
        routes={{
          backBarRoute: appRoutePaths.SealOfBiliteracyList(schoolYear || ''),
          cancelRoute: appRoutePaths.SealOfBiliteracyList(schoolYear || ''),
          createSuccessRoute: (id: string) =>
            appRoutePaths.SealOfBiliteracyInput(id, schoolYear || ''),
        }}
        dataFunctions={{
          loadDataDetailById: fetchSealOfBiliteracyById,
          loadFormDetail: loadFormDetailThunk,
          setEntryMode: (mode: DataEntryMode) => ({
            type: 'setDataEntryMode',
            payload: mode,
          }),
          setData: setDetail,
          clearData: clearDetail,
          onCreate: async (formValues: SealOfBiliteracyModel) => {
            const duplicateCheck = checkForDuplicate(formValues, false);
            if (duplicateCheck.isDuplicate) {
              setDuplicateErrorMessage(duplicateCheck.errorMessage);
              setShowDuplicateErrorModal(true);
              return {
                status: 400,
                data: null,
                error: {
                  type: 'ValidationError',
                  title: 'Duplicate Entry',
                  status: 400,
                  detail: duplicateCheck.errorMessage,
                },
                aborted: false,
              } as ApiResponseModel<unknown>;
            }

            const formattedFormValues = formatNumericMeasures(formValues);
            const formDataWithLeaId = {
              ...formattedFormValues,
              leaIdentifier:
                formattedFormValues.leaIdentifier ||
                selectedDistrict.districtId ||
                '',
              districtName:
                formattedFormValues.districtName || selectedDistrict.name || '',
              schoolName: formattedFormValues.schoolName || '',
              schoolIdentifier: formattedFormValues.schoolIdentifier || '',
            };
            const result = await postSealOfBiliteracy(formDataWithLeaId);

            if (result.status >= 200 && result.status < 300) {
              resetFormState();
            }

            return result;
          },
          onUpdate: async (formValues: SealOfBiliteracyModel) => {
            const duplicateCheck = checkForDuplicate(formValues, true);
            if (duplicateCheck.isDuplicate) {
              setDuplicateErrorMessage(duplicateCheck.errorMessage);
              setShowDuplicateErrorModal(true);
              return {
                status: 400,
                data: null,
                error: {
                  type: 'ValidationError',
                  title: 'Duplicate Entry',
                  status: 400,
                  detail: duplicateCheck.errorMessage,
                },
                aborted: false,
              } as ApiResponseModel<unknown>;
            }

            const formattedFormValues = formatNumericMeasures(formValues);
            const formDataWithLeaId = {
              ...formattedFormValues,
              leaIdentifier:
                formattedFormValues.leaIdentifier ||
                selectedDistrict.districtId ||
                '',
              districtName:
                formattedFormValues.districtName || selectedDistrict.name || '',
              schoolName: formattedFormValues.schoolName || '',
              schoolIdentifier: formattedFormValues.schoolIdentifier || '',
            };
            const result = await putSealOfBiliteracy(formDataWithLeaId);

            if (result.status >= 200 && result.status < 300) {
              resetFormState();
            }

            return result;
          },
        }}
        formDetails={{
          validationSchema: dynamicValidationSchema,
          initialFormValues: initialFormValues,
          isFormModelEmpty: false,
        }}
        config={{
          dataEntryMode: detail ? DataEntryMode.EDIT : DataEntryMode.NEW,
          objectVerbiage: 'Seal of Biliteracy',
          pageHeader: 'Student',
          supportedNonGenericErrors: supportedNonGenericErrors,
        }}
        DEV_MODE={GLOBAL_DEBUG_MODE}
      >
        <SealOfBiliteracyLayout schoolYear={schoolYear} />
      </ManualInputPage>
      {showDuplicateErrorModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showDuplicateErrorModal}
          onClose={() => setShowDuplicateErrorModal(false)}
          title={'Duplicate Entry'}
        >
          <div className="confirm-container">
            <p data-testid="duplicate-entry-error-message">
              {duplicateErrorMessage}
            </p>
            <div className="button-row">
              <ActionButton
                classes="button--secondary"
                onClick={() => setShowDuplicateErrorModal(false)}
                dataTestId="ok-button"
                tooltipText={'OK'}
              >
                <span>OK</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const SealOfBiliteracyInput = (): ReactElement => {
  return (
    <SealOfBiliteracyProvider>
      <SealOfBiliteracyInputContent />
    </SealOfBiliteracyProvider>
  );
};

export default SealOfBiliteracyInput;
